import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Star, Clock, Briefcase, CheckCircle, AlertTriangle,
  TrendingUp, ThumbsUp, ThumbsDown, Send, Building2, Receipt,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import type { ContractorScorecard } from "@/hooks/useContractorScorecards";
import { formatStandardTimestamp } from "@/lib/dateFormat";

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function fixed(value: unknown, digits = 1, fallback = "0.0"): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : fallback;
}

function RateCell({ value, thresholds }: { value: number; thresholds: [number, number] }) {
  const color =
    value >= thresholds[0] ? "text-success" : value >= thresholds[1] ? "text-warning" : "text-destructive";
  return <span className={`font-bold ${color}`}>{value}%</span>;
}

function ListPagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>
      <span className="text-[10px] text-muted-foreground">{page}/{totalPages}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function StatBox({ label, value, sub, icon: Icon, color = "text-muted-foreground" }: {
  label: string; value: string | number; sub?: string; icon: React.ElementType; color?: string;
}) {
  return (
    <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
      <Icon className={`h-3.5 w-3.5 mx-auto mb-1 ${color}`} />
      <p className="text-base font-bold leading-none">{value}</p>
      <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mt-0.5">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

/** Full scorecard for individual profile pages */
export function ContractorScorecardFull({ scorecard }: { scorecard: ContractorScorecard }) {
  const s = scorecard;
  const issueRate = asNumber(s.issueRate);
  const contractorRating = asNumber(s.contractor?.rating);
  const responsivenessRating = asNumber(s.contractor?.responsivenessRating);
  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Performance Scorecard
        </h2>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
          <StatBox label="Response Rate" value={`${s.responseRate}%`} icon={Send} color={s.responseRate >= 80 ? "text-success" : "text-warning"} />
          <StatBox label="Acceptance Rate" value={`${s.acceptanceRate}%`} icon={ThumbsUp} color={s.acceptanceRate >= 70 ? "text-success" : "text-warning"} />
          <StatBox label="Rejection Rate" value={`${s.rejectionRate}%`} icon={ThumbsDown} color={s.rejectionRate <= 20 ? "text-success" : "text-warning"} />
          <StatBox label="Bid Rate" value={`${s.bidParticipationRate}%`} icon={TrendingUp} color="text-primary" />
          <StatBox label="Issue Rate" value={fixed(issueRate)} sub="per job" icon={AlertTriangle} color={issueRate <= 0.5 ? "text-success" : "text-warning"} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <StatBox label="Assigned Jobs" value={s.assignedJobCount} icon={Briefcase} color="text-primary" />
          <StatBox label="Completed" value={s.completedJobCount} icon={CheckCircle} color="text-success" />
          <StatBox label="Total Issues" value={s.totalIssues} icon={AlertTriangle} color={s.totalIssues > 0 ? "text-warning" : "text-success"} />
          <StatBox label="Open Issues" value={s.openIssues} icon={AlertTriangle} color={s.openIssues > 0 ? "text-destructive" : "text-success"} />
          <StatBox label="Credits" value={s.totalCredits > 0 ? `$${s.creditAmount.toLocaleString()}` : "—"} sub={s.totalCredits > 0 ? `${s.totalCredits} records · $${s.avgCreditPerJob}/job` : undefined} icon={Receipt} color={s.totalCredits > 0 ? "text-warning" : "text-success"} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatBox label="Quality" value={fixed(contractorRating)} icon={Star} color="text-warning" />
          <StatBox label="Speed" value={fixed(responsivenessRating)} icon={Clock} color="text-primary" />
          <StatBox label="Opportunities" value={s.totalOpps} icon={Send} color="text-muted-foreground" />
          <StatBox label="Last Activity" value={formatStandardTimestamp(s.recentActivity)} icon={Clock} color="text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

/** Compact comparison table for reporting dashboard */
export function ContractorScorecardTable({
  scorecards,
  page = 1,
  totalPages = 1,
  onPageChange,
}: {
  scorecards: ContractorScorecard[];
  page?: number;
  totalPages?: number;
  onPageChange?: (next: number) => void;
}) {
  const safeScorecards = scorecards.filter(
    (s) =>
      !!s &&
      !!s.contractor &&
      typeof s.contractor.id === "string" &&
      s.contractor.id.length > 0,
  );

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4" /> Contractor Performance Scorecards
        </h2>

        {safeScorecards.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">No active contractors</p>
        ) : (
          <div className="border rounded-lg overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/40 border-b">
                  <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Contractor</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">
                    <Star className="h-3 w-3 mx-auto" />
                  </th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">
                    <Clock className="h-3 w-3 mx-auto" />
                  </th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">Opps</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">Resp%</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">Acpt%</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">Rej%</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden md:table-cell">Bid%</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">Jobs</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">Done</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground">Issues</th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden sm:table-cell">
                    <Receipt className="h-3 w-3 mx-auto" />
                  </th>
                  <th className="text-center py-2.5 px-2 font-medium text-muted-foreground hidden md:table-cell">Rate</th>
                  <th className="text-right py-2.5 px-3 font-medium text-muted-foreground hidden lg:table-cell">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {safeScorecards.map((s) => (
                  <tr key={s.contractor.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-3">
                      <Link to={`/contractors/${s.contractor.id}`} className="hover:text-primary transition-colors">
                        <p className="font-semibold">{s.contractor.companyName}</p>
                        <p className="text-[10px] text-muted-foreground">{s.contractor.tradeType} · {s.contractor.serviceArea}</p>
                      </Link>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className="font-bold text-warning">{s.contractor.rating}</span>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className="font-bold text-primary">{s.contractor.responsivenessRating}</span>
                    </td>
                    <td className="py-2 px-2 text-center font-medium hidden sm:table-cell">{s.totalOpps}</td>
                    <td className="py-2 px-2 text-center"><RateCell value={s.responseRate} thresholds={[80, 50]} /></td>
                    <td className="py-2 px-2 text-center"><RateCell value={s.acceptanceRate} thresholds={[70, 40]} /></td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell"><RateCell value={s.rejectionRate} thresholds={[100, 80]} /></td>
                    <td className="py-2 px-2 text-center hidden md:table-cell">
                      <span className="font-bold text-primary">{s.bidParticipationRate}%</span>
                    </td>
                    <td className="py-2 px-2 text-center font-bold">{s.assignedJobCount}</td>
                    <td className="py-2 px-2 text-center font-medium text-success hidden sm:table-cell">{s.completedJobCount}</td>
                    <td className="py-2 px-2 text-center">
                      {s.openIssues > 0 ? (
                        <span className="inline-flex items-center gap-0.5 text-destructive font-bold">
                          <AlertTriangle className="h-3 w-3" /> {s.openIssues}
                        </span>
                      ) : (
                        <span className="text-success font-medium">✓</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell">
                      {s.creditAmount > 0 ? (
                        <span className="font-bold text-warning">${s.creditAmount.toLocaleString()}</span>
                      ) : (
                        <span className="text-success font-medium">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center hidden md:table-cell">
                      <span className={`font-medium ${asNumber(s.issueRate) <= 0.5 ? "text-success" : "text-warning"}`}>
                        {fixed(s.issueRate)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-muted-foreground hidden lg:table-cell">{formatStandardTimestamp(s.recentActivity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPagination
              page={page}
              totalPages={totalPages}
              onChange={onPageChange ?? (() => undefined)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
